import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '@/i18n/I18nContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MessageSquare } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function ResetPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.new_password || !formData.confirm_password) {
      toast.error(t('auth.fillAllFields'));
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      toast.error(t('auth.passwordMismatch'));
      return;
    }

    if (formData.new_password.length < 8) {
      toast.error(t('auth.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: formData.new_password,
      });
      
      toast.success(t('auth.passwordResetSuccess'));
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.tokenInvalid'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <Alert variant="destructive">
              <AlertDescription>
                {t('auth.invalidLink')}
              </AlertDescription>
            </Alert>
            <Button
              className="w-full mt-4"
              onClick={() => navigate('/forgot-password')}
              data-testid="button-request-new-link"
            >
              {t('auth.requestNewLink')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center space-x-2 mb-8">
          <MessageSquare className="w-10 h-10 text-emerald-600" />
          <span className="text-3xl font-bold text-slate-900">EVA AI</span>
        </div>

        <Card data-testid="reset-password-form">
          <CardHeader className="text-center">
            <div className="flex justify-end mb-2">
              <LanguageSelector />
            </div>
            <CardTitle className="text-2xl">{t('auth.setNewPassword')}</CardTitle>
            <CardDescription>
              {t('auth.setNewPasswordDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new_password">{t('auth.newPassword')}</Label>
                <Input
                  id="new_password"
                  name="new_password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.new_password}
                  onChange={handleChange}
                  data-testid="input-new-password"
                  disabled={loading}
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm_password">{t('auth.confirmPassword')}</Label>
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  data-testid="input-confirm-password"
                  disabled={loading}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={loading}
                data-testid="button-reset"
              >
                {loading ? t('auth.updating') : t('auth.setNewPassword')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
